import {USG_STATE} from "@wesstron/utils/Api/constants/usgStates";
import {Form, Formik, useField} from "formik";
import moment from "moment";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {Fade} from "react-bootstrap";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getPossibleValuesForColumns} from "../../api/results/getPossibleValuesForColumns";
import {PARTURITION_DIFFICULTY} from "../../constans/eventTypes";
import {FilterTypes} from "../../constans/filter";
import sowState from "../../constans/sowState";
import useEventEmitter from "../../hooks/useEventEmitter";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import {
    getFallReasons,
    getMedicines,
    getRacesData,
    getSelectionReasons,
    getSellReasons,
    getSuppliers,
} from "../../selectors/dictionarySelectors";
import {SowStatuses} from "../../utils/AnimalsUtils";
import {textContrast} from "../../utils/ColorUtils";
import {REMOVE_TECHNOLOGY_GROUP_REASONS} from "../../utils/TechnologyGroupUtils";
import {filterMap} from "../../utils/Utils";
import {removeReasonCodeToTranslationKey} from "../../views/new-farm-view/tabs/results-renderers/ResultsReportFormatters";
import Button from "../basics/button/Button";
import Checkbox from "../basics/checkbox/Checkbox";
import InfoBox from "../basics/info-box/InfoBox";
import Input from "../basics/input/Input";
import LabeledInput from "../basics/input/labeled-input/LabeledInput";
import LabeledSelect from "../basics/select/labeled-select/LabeledSelect";
import Tooltip from "../basics/tooltip/Tooltip";
import "./_sql-table-header-filter-component.scss";
import {useTransition} from "react";
import NotFound from "../NotFound";

function NumberHelp() {
    const {t} = useTranslation();

    return (
        <div>
            <p>{t("filterHelpMathInfo")}</p>
            <div>
                <span className="fw-bold">&gt;</span> -{" "}
                {t("filterHelpGreaterThan")} &gt;5
            </div>
            <div>
                <span className="fw-bold">&lt;</span> -{" "}
                {t("filterHelpLowerThan")} &lt;5
            </div>
            <div>
                <span className="fw-bold">&gt;=</span> -{" "}
                {t("filterHelpGreatherThanOrEquals")} &gt;=5
            </div>
            <div>
                <span className="fw-bold">&lt;=</span> -{" "}
                {t("filterHelpLowerThanOrEquals")} &lt;=5
            </div>
            <p>{t("filterHelpRangeInfo")}</p>
            <div className="mb-2">
                {t("filterHelpRangeOpenBoth", {from: 0, to: 3})}
            </div>
            <div className="mb-2">
                {t("filterHelpRangeClosedRight", {from: 0, to: 3})}
            </div>
            <div className="mb-2">
                {t("filterHelpRangeClosedLeft", {from: 0, to: 3})}
            </div>
            <div>{t("filterHelpRangeClosedBoth", {from: 0, to: 3})}</div>
        </div>
    );
}

function DateHelp() {
    const {t} = useTranslation();

    const today = moment().format("L");
    const subtracted = moment().subtract(3, "days").format("L");

    return (
        <div>
            <p>{t("filterHelpMathInfo")}</p>
            <div>
                <span className="fw-bold">&gt;</span> -{" "}
                {t("filterHelpGreaterThan")} &gt;{today}
            </div>
            <div>
                <span className="fw-bold">&lt;</span> -{" "}
                {t("filterHelpLowerThan")} &lt;{today}
            </div>
            <div>
                <span className="fw-bold">&gt;=</span> -{" "}
                {t("filterHelpGreatherThanOrEquals")} &gt;={today}
            </div>
            <div>
                <span className="fw-bold">&lt;=</span> -{" "}
                {t("filterHelpLowerThanOrEquals")} &lt;={today}
            </div>
            <p>{t("filterHelpRangeInfo")}</p>
            <div className="mb-2">
                {t("filterHelpRangeOpenBoth", {from: subtracted, to: today})}
            </div>
            <div className="mb-2">
                {t("filterHelpRangeClosedRight", {from: subtracted, to: today})}
            </div>
            <div className="mb-2">
                {t("filterHelpRangeClosedLeft", {from: subtracted, to: today})}
            </div>
            <div>
                {t("filterHelpRangeClosedBoth", {from: subtracted, to: today})}
            </div>
            <p>{t("filterHelpDateQuestionMark")}</p>
        </div>
    );
}

function StringHelp() {
    const {t} = useTranslation();

    return (
        <div>
            <p>{t("filterHelpString")}</p>
            <p>{t("filterHelpStringSplit")}</p>
        </div>
    );
}

function StringFilter({header}) {
    const {t} = useTranslation();

    const [input] = useField("value");

    const FilterInfo = useMemo(() => {
        switch (header.filterType) {
            case FilterTypes.NUMBER:
                return NumberHelp;
            case FilterTypes.DATE:
                return DateHelp;
            case FilterTypes.STRING:
                return StringHelp;
            default:
                return null;
        }
    }, [header.filterType]);

    return (
        <div className="sql-table-header-filter-component-string">
            <div>
                <LabeledInput
                    label={t("value")}
                    type="text"
                    {...input}
                    value={input.value || ""}
                    onChangeWithEvent
                />
            </div>
            {FilterInfo && (
                <div>
                    <Tooltip
                        tooltipContent={<FilterInfo />}
                        placement="auto"
                        type="info">
                        <i className="fa-solid fa-info-circle" />
                    </Tooltip>
                </div>
            )}
        </div>
    );
}

function BooleanFilter() {
    const {t} = useTranslation();

    const [input, , handlers] = useField("value");

    const options = useMemo(
        () => [
            {value: true, name: t("yes")},
            {value: false, name: t("no")},
        ],
        [t]
    );

    return (
        <LabeledSelect
            label={t("value")}
            value={input.value || ""}
            options={options}
            onChange={handlers.setValue}
            clearButton={false}
        />
    );
}

function DictionaryRow({name, label, color, checked, onChange: onChangeProps}) {
    const onChange = useCallback(
        (value) => {
            onChangeProps(value, name);
        },
        [name, onChangeProps]
    );

    return (
        <li>
            <Checkbox
                id={name}
                checked={checked}
                onChange={onChange}
                label=""
            />
            <span
                className={color ? "ps-1 pe-1 rounded" : null}
                style={
                    color
                        ? {background: color, color: textContrast(color)}
                        : null
                }>
                {label}
            </span>
        </li>
    );
}

function DictionaryComponent({items}) {
    const {t} = useTranslation();

    const [search, setSearch] = useState("");
    const [filteredItems, setFilteredItems] = useState(items);

    const [, startTransition] = useTransition();

    const [input, , handlers] = useField("value");

    const isChecked = useCallback(
        (name) => {
            return input.value?.includes(name) ?? false;
        },
        [input.value]
    );

    const onChange = useCallback(
        (value, name) => {
            const copy = input.value?.slice() ?? [];
            if (value) {
                copy.push(name);
            } else {
                const index = copy.indexOf(name);
                if (index !== -1) {
                    copy.splice(index, 1);
                }
            }
            if (copy.length === 0) {
                handlers.setValue(null);
            } else {
                handlers.setValue(copy);
            }
        },
        [input.value, handlers]
    );

    const isAllSelected = useMemo(() => {
        if (!input.value) return false;
        return filteredItems.every((item) => input.value.includes(item.name));
    }, [input.value, filteredItems]);

    const onSelectAllClick = useCallback(() => {
        if (isAllSelected) {
            handlers.setValue(null);
            return;
        }
        const allNames = filteredItems.map((item) => item.name);
        handlers.setValue(allNames);
    }, [handlers, filteredItems, isAllSelected]);

    const onChangeSearch = useCallback(
        (value) => {
            setSearch(value || "");
            startTransition(() => {
                const lower = (value || "").toLowerCase();
                const newFiltered = items.filter((item) =>
                    item.label.toLowerCase().includes(lower)
                );
                setFilteredItems(newFiltered);
            });
        },
        [items]
    );

    return (
        <>
            <div>
                <Input
                    type="search"
                    placeholder={t("costsView.filters.search")}
                    className="mb-2"
                    value={search}
                    onChange={onChangeSearch}
                />
            </div>
            {filteredItems.length === 0 && <NotFound />}
            {filteredItems.length > 0 && (
                <ul className="sql-table-header-filter-component-dictionary">
                    <li>
                        <Checkbox
                            id={"select-all"}
                            checked={isAllSelected}
                            onChange={onSelectAllClick}
                            label={t("selectAll")}
                        />
                    </li>
                    {filteredItems.map((item) => (
                        <DictionaryRow
                            key={item.name}
                            name={item.name}
                            label={item.label}
                            color={item.color}
                            checked={isChecked(item.name)}
                            onChange={onChange}
                        />
                    ))}
                </ul>
            )}
        </>
    );
}

function MedicineFilter() {
    const medicines = useSelector(getMedicines);

    const items = useMemo(
        () =>
            medicines
                .map((medicine) => ({
                    name: medicine.WordID,
                    label: medicine.WData.Name,
                    color: medicine.WData.Color,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [medicines]
    );

    return <DictionaryComponent items={items} />;
}

function ParturitionLevelFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {name: t("low"), value: PARTURITION_DIFFICULTY.LOW},
            {
                name: t("medium"),
                value: PARTURITION_DIFFICULTY.MEDIUM,
            },
            {name: t("hard"), value: PARTURITION_DIFFICULTY.HARD},
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function RaceFilter() {
    const races = useSelector(getRacesData);

    const items = useMemo(
        () =>
            races
                .map((race) => ({
                    name: race.ID,
                    label: race.Value,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [races]
    );

    return <DictionaryComponent items={items} />;
}

function RaceCodeFilter() {
    const races = useSelector(getRacesData);

    const items = useMemo(
        () =>
            races
                .map((race) => ({
                    name: race.ID,
                    label: race.Code,
                    color: race.Color,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [races]
    );

    return <DictionaryComponent items={items} />;
}

function FallReasonFilter() {
    const fallReasons = useSelector(getFallReasons);

    const items = useMemo(
        () =>
            fallReasons.WData.map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [fallReasons]
    );

    return <DictionaryComponent items={items} />;
}

function USGResultFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {name: t("events.usgEvent.negative"), label: USG_STATE.NEGATIVE},
            {name: t("events.usgEvent.positive"), label: USG_STATE.POSITIVE},
            {name: t("events.usgEvent.toRepeat"), label: USG_STATE.REPEAT},
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function SowStateFilter() {
    const {t} = useTranslation();

    const items = useMemo(() => {
        return Object.keys(sowState).map((key) => ({
            name: key,
            label: t(`sowStatus.${sowState[key]}`),
        }));
    }, [t]);

    return <DictionaryComponent items={items} />;
}

function SupplierFilter() {
    const suppliers = useSelector(getSuppliers);

    const items = useMemo(
        () =>
            suppliers.WData.map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [suppliers]
    );

    return <DictionaryComponent items={items} />;
}

function TechnologyGroupRemovalFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => {
            return filterMap(
                Object.values(REMOVE_TECHNOLOGY_GROUP_REASONS),
                (code) => {
                    const translationKey =
                        removeReasonCodeToTranslationKey(code);
                    if (!translationKey) return null;
                    return {
                        name: code,
                        label: t(translationKey),
                    };
                }
            );
        },
        // there was an inconsistency between the chart and filer input (missing options)
        // make sure the filter input is always up to date by using `REMOVE_TECHNOLOGY_GROUP_REASONS`
        // () => [
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.INSEMINATION_OTHER_WEEK,
        //         label: t("inseminationInNextWeek"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.NEGATIVE_USG,
        //         label: t("animalDocuments.removeReason.negativeUSG"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PREGNANCY,
        //         label: t("events.parturitionEvent.noPregnancy"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.SEPARATION,
        //         label: t("eventTypes.S"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.FALL,
        //         label: t("eventTypes.F"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.SELL,
        //         label: t("eventTypes.L"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.NEXT_CYCLE,
        //         label: t("foundNextCycle"),
        //     },
        //     {
        //         name: REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PARTURITION_EVENT,
        //         label: t("noParturitionEvent"),
        //     },
        //     // {name: REMOVE_TECHNOLOGY_GROUP_REASONS.IS_MOMMY, label: "Mamkowanie"},
        // ],
        [t]
    );
    return <DictionaryComponent items={items} />;
}

function RemovedReasonFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {
                name: SowStatuses.DEAD,
                label: t(`sowStatus.${SowStatuses.DEAD}`),
            },
            {
                name: SowStatuses.SOLD,
                label: t(`sowStatus.${SowStatuses.SOLD}`),
            },
            {
                name: SowStatuses.REMOVED,
                label: t(`sowStatus.${SowStatuses.REMOVED}`),
            },
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function AsyncDictionaryFilter({ResultID, filterColumn}) {
    const {t} = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => {
        async function getData() {
            try {
                setLoading(true);
                const res = await getPossibleValuesForColumns(
                    ResultID,
                    filterColumn
                );
                setData(res);
            } catch (e) {
                console.error(e);
                setError(true);
            } finally {
                setLoading(false);
            }
        }

        getData();
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    const items = useMemo(() => {
        return data.map((item) => ({
            name: item ?? "<empty>",
            label: item ?? t("none"),
        }));
    }, [data, t]);

    if (loading) {
        return (
            <div className="text-center">
                <i className="fa-solid fa-circle-notch fa-spin" />
            </div>
        );
    }
    if (error) {
        return (
            <InfoBox outlined boxColor="error">
                <i className="fa-solid fa-exclamation-circle me-2" />
                <span>{t("newSettings.devices.settingsInfo.noData")}</span>
            </InfoBox>
        );
    }
    return <DictionaryComponent items={items} />;
}

function SellReasonFilter() {
    const sellReasons = useSelector(getSellReasons);

    const items = useMemo(
        () =>
            sellReasons.map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [sellReasons]
    );

    return <DictionaryComponent items={items} />;
}

function SelectionReasonFilter() {
    const selectionReasons = useSelector(getSelectionReasons);

    const items = useMemo(
        () =>
            (selectionReasons?.WData || []).map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [selectionReasons]
    );

    return <DictionaryComponent items={items} />;
}

function SQLTableFilterComponentForm({
    handleSubmit,
    resetForm,
    header,
    filterType,
    ResultID,
    filterColumn,
}) {
    const {t} = useTranslation();

    const onResetClick = useCallback(() => {
        resetForm({values: {type: filterType}});
        handleSubmit();
    }, [resetForm, filterType, handleSubmit]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.keyCode === 46) {
                // del
                onResetClick();
            }
        },
        [onResetClick]
    );

    useEventEmitter("keydown", onKeyDown);

    const Component = useMemo(() => {
        switch (header.filterType) {
            case FilterTypes.BOOLEAN:
                return BooleanFilter;
            case FilterTypes.MEDICINE:
                return MedicineFilter;
            case FilterTypes.PARTURITION_LEVEL:
                return ParturitionLevelFilter;
            case FilterTypes.RACE:
                return RaceFilter;
            case FilterTypes.RACE_CODE:
                return RaceCodeFilter;
            case FilterTypes.FALL_REASON:
                return FallReasonFilter;
            case FilterTypes.USG_RESULT:
                return USGResultFilter;
            case FilterTypes.SOW_STATE:
                return SowStateFilter;
            case FilterTypes.SUPPLIER:
                return SupplierFilter;
            case FilterTypes.TECHNOLOGY_GROUP_REMOVAL:
                return TechnologyGroupRemovalFilter;
            case FilterTypes.REMOVE_REASON:
                return RemovedReasonFilter;
            case FilterTypes.ASYNC_DICTIONARY:
                return AsyncDictionaryFilter;
            case FilterTypes.SELL_REASON:
                return SellReasonFilter;
            case FilterTypes.SELECTION_REASON:
                return SelectionReasonFilter;
            default:
                return StringFilter;
        }
    }, [header.filterType]);

    return (
        <Form>
            <Component
                ResultID={ResultID}
                header={header}
                filterColumn={filterColumn}
            />
            <div className="sql-table-header-filter-component-apply">
                <small>
                    <Button
                        buttonStyle="text"
                        className="me-3"
                        type="button"
                        onClick={onResetClick}>
                        {t("clear")}
                    </Button>
                </small>
                <Button buttonColor="success" onClick={handleSubmit}>
                    {t("apply")}
                </Button>
            </div>
        </Form>
    );
}

function SQLTableFilterComponentRenderer({
    show,
    setShowFilter,
    filters,
    setFilters,
    header,
    style,
    ResultID,
}) {
    const container = useRef(null);

    const filterColumn = header.filterColumn || header.field;

    const filterType = useMemo(() => {
        if (
            [
                FilterTypes.STRING,
                FilterTypes.NUMBER,
                FilterTypes.DATE,
                FilterTypes.BOOLEAN,
            ].includes(header.filterType)
        )
            return header.filterType;
        return "dictionary";
    }, [header.filterType]);

    const initialValues = useMemo(() => {
        if (filters[filterColumn]) {
            return filters[filterColumn];
        }
        return {type: filterType};
    }, [filterColumn, filters, filterType]);

    const onClickOutside = useCallback(() => {
        setShowFilter(false);
    }, [setShowFilter]);

    useOnClickOutside(container, onClickOutside);

    const onSubmit = useCallback(
        (values, formikBag) => {
            console.log(values, formikBag);
            setFilters((prev) => {
                const copy = {...prev};
                if (values.value) {
                    copy[filterColumn] = values;
                } else {
                    delete copy[filterColumn];
                }
                return copy;
            });
            setShowFilter(false);
        },
        [setShowFilter, setFilters, filterColumn]
    );

    return (
        <Fade in={show} mountOnEnter unmountOnExit>
            <div
                className="sql-table-header-filter-component"
                style={{...style}}
                ref={container}>
                <Formik onSubmit={onSubmit} initialValues={initialValues}>
                    {(props) => (
                        <SQLTableFilterComponentForm
                            {...props}
                            header={header}
                            filterType={filterType}
                            ResultID={ResultID}
                            filterColumn={filterColumn}
                        />
                    )}
                </Formik>
            </div>
        </Fade>
    );
}

export default function SQLTableFilterComponent({
    show,
    parent,
    setShowFilter,
    filters,
    setFilters,
    header,
    ResultID, // uzywane tylko w przypadku FilterType.ASYNC_DICTIONARY, bo musi pobrac dane
}) {
    const [style, setStyle] = useState({left: 0, top: 0});

    const modal = useMemo(() => {
        if (parent.current) {
            return parent.current.closest(".modal-content");
        }
        return null;
    }, [parent.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const setPositionForParent = useCallback(() => {
        if (!parent.current) return;
        const rect = parent.current.getBoundingClientRect();
        const {innerWidth, innerHeight} = window;
        const newStyle = {};
        if (rect.left > innerWidth / 2) {
            newStyle.right = innerWidth - rect.left - rect.width;
        } else {
            newStyle.left = rect.left;
        }
        const scrollY = modal ? 0 : window.scrollY;
        if (rect.top > innerHeight / 2) {
            newStyle.bottom = innerHeight - rect.top - rect.height + scrollY;
        } else {
            newStyle.top = rect.top + rect.height + scrollY;
        }
        setStyle(newStyle);
    }, [parent, modal]);

    useLayoutEffect(() => {
        if (!show) return;
        setPositionForParent();
    }, [show, setPositionForParent]);

    return createPortal(
        <SQLTableFilterComponentRenderer
            filters={filters}
            header={header}
            setFilters={setFilters}
            setShowFilter={setShowFilter}
            show={show}
            style={style}
            ResultID={ResultID}
        />,
        modal || document.getElementById("root")
    );
}
